<!--
  DistributorReportsPage -- the Reports page under Reporting.

  Card: Reports page under Reporting (distributor app).
  Route: /distributor/reporting/reports (capability distributor.reporting.read)

  Joins the Reporting group beside Orders, Performance and Payouts. Where
  ReportBuilder.vue is embedded in the store-scoped flow (it receives a
  pre-selected campaign), this page is the standalone entry point: pick a
  store, optionally narrow to one batch and a date range, choose a
  format, generate.

  FILTER CONTRACT (Jake, 2026-09-19)
  ==================================

  * Date range defaults to NO filter. The page must never quietly scope to a
    default window -- a report that silently omits orders is worse than one
    that refuses to run, because nothing on screen says anything is missing.
  * Store is a combo-box and one MUST be chosen. "All stores" was explicitly
    descoped: there is no cross-store aggregation here, and no option in the
    list offers it.
  * Batch appears only once a store is selected, and lists only RELEASED
    batches.
  * Format is CSV / XLSX / PDF / JSON, CSV by default.

  WHY THE BATCH LIST IS SERVER-FILTERED, NOT FILTERED HERE
  ========================================================

  This page calls `getReleasedBatches`, never `getBatches` plus a
  `.filter()`. A filtered dropdown is a convenience, not a guard: the batch
  id travels in the URL of the generate call, so the rule that actually
  protects a decorator from receiving a PO for unauthorized work lives at
  the API (`src/services/report_batch_guard.py`), and both the list and the
  guard read it. Filtering here as well would be a second definition of one
  fact, free to drift from the first.
-->
<template>
  <section class="drep" data-testid="reports-page">
    <header class="drep__head">
      <h1 class="drep__title">Reports</h1>
      <p class="drep__lede">
        Choose a store, then narrow by batch or date range.
      </p>
    </header>

    <v-form class="drep__filters" @submit.prevent>
      <v-row align="start">
        <!-- Store: required. No all-stores option by design. -->
        <v-col cols="12" sm="6" md="4">
          <label class="drep__label" for="reports-store">Store</label>
          <v-autocomplete
            id="reports-store"
            data-testid="reports-store-select"
            :items="storeOptions"
            :model-value="selectedStoreId"
            :loading="storesPending"
            item-title="name"
            item-value="id"
            placeholder="Select a store"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:model-value="onStoreSelected"
          />
        </v-col>

        <!--
          Batch: only once a store is chosen. Before that there is nothing
          to scope a batch list to, and we deliberately do not fetch one.
        -->
        <v-col v-if="selectedStoreId" cols="12" sm="6" md="4">
          <label class="drep__label" for="reports-batch">Batch</label>
          <v-select
            id="reports-batch"
            data-testid="reports-batch-select"
            :items="batchOptions"
            :model-value="selectedBatchId"
            :loading="batchesPending"
            :disabled="batches.length === 0"
            item-title="label"
            item-value="id"
            placeholder="All batches"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:model-value="onBatchSelected"
          />
          <p
            v-if="!batchesPending && batches.length === 0"
            class="drep__hint"
            data-testid="reports-no-batches"
          >
            This store has no batches yet. Reports can still be run
            across the whole store.
          </p>
        </v-col>

        <!-- Format -->
        <v-col cols="12" sm="6" md="4">
          <label class="drep__label" for="reports-format">Format</label>
          <v-select
            id="reports-format"
            data-testid="reports-format-select"
            :items="formats"
            :model-value="selectedFormat"
            item-title="label"
            item-value="value"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:model-value="onFormatSelected"
          />
        </v-col>
      </v-row>

      <v-row align="start">
        <!--
          Date range. Both ends start empty and that means UNFILTERED --
          see the filter contract above.
        -->
        <v-col cols="12" sm="6" md="4">
          <label class="drep__label" for="reports-start">From</label>
          <v-text-field
            id="reports-start"
            data-testid="reports-start-date"
            type="date"
            :model-value="startDate"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:model-value="onStartDateChanged"
          />
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <label class="drep__label" for="reports-end">To</label>
          <v-text-field
            id="reports-end"
            data-testid="reports-end-date"
            type="date"
            :model-value="endDate"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:model-value="onEndDateChanged"
          />
        </v-col>

        <!-- Report type, populated for the chosen store + batch. -->
        <v-col v-if="selectedStoreId" cols="12" sm="6" md="4">
          <label class="drep__label" for="reports-report">Report</label>
          <v-select
            id="reports-report"
            data-testid="reports-report-select"
            :items="reportOptions"
            :model-value="selectedReportSlug"
            :loading="reportsPending"
            item-title="name"
            item-value="slug"
            placeholder="Select a report"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:model-value="onReportSelected"
          />
        </v-col>
      </v-row>

      <p
        v-if="dateRangeError"
        class="drep__error"
        data-testid="reports-date-error"
        role="alert"
      >
        {{ dateRangeError }}
      </p>

      <p
        v-if="loadError"
        class="drep__error"
        data-testid="reports-load-error"
        role="alert"
      >
        {{ loadError }}
      </p>

      <div class="drep__actions">
        <v-btn
          data-testid="reports-generate"
          color="primary"
          :disabled="!canGenerate"
          :loading="generating"
          @click="generate"
        >
          Generate report
        </v-btn>

        <span
          v-if="hasDateFilter"
          class="drep__hint"
          data-testid="reports-date-active"
        >
          Date filter active
        </span>
      </div>
    </v-form>
  </section>
</template>

<script>
import { getReleasedBatches } from "@/services/batchService";
import { getReportCampaignList } from "@/services/campaignService";
import { getReports, downloadReport } from "@/services/reportService";

export default {
  name: "DistributorReportsPage",

  data() {
    return {
      stores: [],
      storesPending: false,

      // null, not "all": there is no cross-store mode.
      selectedStoreId: null,

      batches: [],
      batchesPending: false,
      selectedBatchId: null,

      reports: [],
      reportsPending: false,
      selectedReportSlug: null,

      // Empty means UNFILTERED. Do not default these to a window.
      startDate: "",
      endDate: "",

      // CSV first and default (Jake, 2026-09-23). It is what a person opens
      // in a spreadsheet, and the format the AH demo exercises. PDF was the
      // default only by virtue of being listed first.
      selectedFormat: "csv",
      formats: [
        { value: "csv", label: "CSV" },
        { value: "xlsx", label: "Excel (XLSX)" },
        { value: "pdf", label: "PDF" },
        { value: "json", label: "JSON" },
      ],

      generating: false,
      loadError: "",
    };
  },

  computed: {
    storeOptions() {
      return this.stores;
    },

    batchOptions() {
      // Every row here came from the released-only endpoint. The label
      // carries the release date because a distributor picks a batch by
      // "the one we released on the 14th", not by id.
      return this.batches.map((b) => ({
        id: b.id,
        label: this.batchLabel(b),
      }));
    },

    reportOptions() {
      return this.reports;
    },

    hasDateFilter() {
      return Boolean(this.startDate) || Boolean(this.endDate);
    },

    dateRangeError() {
      if (!this.startDate || !this.endDate) {
        return "";
      }
      if (this.startDate > this.endDate) {
        return "The end date must not be earlier than the start date.";
      }
      return "";
    },

    canGenerate() {
      return Boolean(
        this.selectedStoreId &&
          this.selectedReportSlug &&
          !this.dateRangeError &&
          !this.generating
      );
    },
  },

  mounted() {
    this.loadStores();
  },

  methods: {
    batchLabel(batch) {
      const name = batch.name || `Batch ${batch.id}`;
      const parts = [name];
      if (batch.released_at) {
        parts.push(`released ${batch.released_at}`);
      } else if (batch.created_at) {
        // Since #3711 removed the release-state filter, the picker carries
        // batches that have no released_at. Fall back to the created date so
        // the row keeps the thing a distributor picks it BY, and say
        // "started" rather than "released" so the two are not confusable.
        parts.push(`started ${batch.created_at}`);
      }
      if (batch.distributor_po) {
        parts.push(`PO ${batch.distributor_po}`);
      }
      return parts.join(" - ");
    },

    async loadStores() {
      this.storesPending = true;
      this.loadError = "";
      try {
        const result = await getReportCampaignList();
        this.stores = result.data || [];
      } catch (err) {
        this.loadError = "We could not load your stores. Please reload.";
      } finally {
        this.storesPending = false;
      }
    },

    /**
     * Vuetify 3 note: this is bound to `@update:model-value`, not
     * `@change` -- `@change` does not fire on Vuetify selects (CLAUDE.md).
     */
    async onStoreSelected(storeId) {
      this.selectedStoreId = storeId || null;

      // A batch id belongs to the store it came from. Carrying one across a
      // store change would send the server an id outside the new store's
      // scope, which it refuses -- so clear it here rather than surface a
      // confusing rejection.
      this.selectedBatchId = null;
      this.batches = [];
      this.selectedReportSlug = null;
      this.reports = [];

      if (!this.selectedStoreId) {
        return;
      }

      await Promise.all([this.loadReleasedBatches(), this.loadReports()]);
    },

    async loadReleasedBatches() {
      this.batchesPending = true;
      try {
        const result = await getReleasedBatches(this.selectedStoreId);
        this.batches = result.data || [];
      } catch (err) {
        this.batches = [];
        this.loadError = "We could not load batches for this store.";
      } finally {
        this.batchesPending = false;
      }
    },

    async loadReports() {
      this.reportsPending = true;
      try {
        // "null" is the sentinel the report API uses for "not scoped to a
        // batch" -- it is not a batch id and never names one.
        const batchParam =
          this.selectedBatchId === null ? "null" : this.selectedBatchId;
        const result = await getReports(this.selectedStoreId, batchParam);
        this.reports = result.data || [];
      } catch (err) {
        this.reports = [];
      } finally {
        this.reportsPending = false;
      }
    },

    async onBatchSelected(batchId) {
      this.selectedBatchId = batchId === undefined ? null : batchId;
      // Which reports are available depends on the batch selection.
      this.selectedReportSlug = null;
      await this.loadReports();
    },

    onReportSelected(slug) {
      this.selectedReportSlug = slug || null;
    },

    onFormatSelected(fmt) {
      this.selectedFormat = fmt || "csv";
    },

    onStartDateChanged(value) {
      this.startDate = value || "";
    },

    onEndDateChanged(value) {
      this.endDate = value || "";
    },

    async generate() {
      if (!this.canGenerate) {
        return;
      }
      this.generating = true;
      this.loadError = "";
      try {
        const batchParam =
          this.selectedBatchId === null ? "null" : this.selectedBatchId;
        const store = this.stores.find(
          (s) => String(s.id) === String(this.selectedStoreId)
        );
        // Used only to NAME an xlsx download, which is one whole-workbook
        // file per report type rather than one file per table.
        const reportType = this.reports.find(
          (r) => r.slug === this.selectedReportSlug
        );
        // The date range travels with the request. Until this was wired,
        // the control below validated its input and displayed "Date
        // filter active" while the generated report ignored it entirely.
        // Empty ends are dropped by the service, so a cleared control
        // sends nothing and the report is unfiltered.
        //
        // `downloadReport` resolves the report NAMES from /reports/list/...
        // rather than sending this report TYPE's display label. Sending the
        // label was GH #3692: the server matched nothing, returned 200 with
        // a zero-byte body, and no file was ever produced. It also SAVES
        // the result -- `getReport` only fetched it, so even a correct name
        // downloaded nothing.
        const saved = await downloadReport(
          this.selectedReportSlug,
          this.selectedStoreId,
          this.selectedFormat,
          batchParam,
          {
            storeName: store ? store.name : "",
            reportTypeName: reportType ? reportType.name : "",
            dateRange: { startDate: this.startDate, endDate: this.endDate },
          }
        );
        // Zero files is a real outcome, not a success: this selection
        // produced no content. Saying so is what distinguishes "no data"
        // from the silent no-op this replaced.
        if (!saved) {
          this.loadError =
            "That report has no data for the selected store, batch and dates.";
        }
      } catch (err) {
        this.loadError = "We could not generate that report. Please retry.";
      } finally {
        this.generating = false;
      }
    },
  },
};
</script>

<style scoped>
.drep {
  padding: 24px;
}

.drep__title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.drep__lede {
  color: rgba(0, 0, 0, 0.6);
  margin: 4px 0 16px;
}

.drep__label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.drep__hint {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.8125rem;
  margin: 4px 0 0;
}

.drep__error {
  color: #b00020;
  font-size: 0.875rem;
  margin: 12px 0 0;
}

.drep__actions {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-top: 20px;
}
</style>
